import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent, PointerEvent as ReactPointerEvent } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import {
  AirplaneTilt,
  ArrowLeft,
  ArrowRight,
  ArrowsOut,
  Baby,
  Briefcase,
  ChartBar,
  ChartDonut,
  ChartLine,
  ChatCircleDots,
  CircleNotch,
  FileCsv,
  FileHtml,
  GlobeHemisphereWest,
  Heartbeat,
  House,
  MagnifyingGlass,
  Minus,
  PaperPlaneTilt,
  Plus,
  Sparkle,
  Table,
  Translate,
  UsersThree,
  X,
} from "@phosphor-icons/react";
import type { AtlasIndex, District, Language, LocalDataBundle, LocalLevel, Province } from "./types";

gsap.registerPlugin(useGSAP);

type NumericSeries = { name: string; data: number[] };

interface DataView {
  id: string;
  title: string;
  family: string;
  category: CategoryId;
  categories: string[];
  series: NumericSeries[];
  note?: string;
  raw: unknown;
}

type ViewMode = "chart" | "table";
type ChartType = "donut" | "line" | "bar";
type ChatSource = { title: string; url: string; description: string };
type ChatMessage = { role: "user" | "assistant"; content: string; sources?: ChatSource[] };

const safeExternalUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.toString() : "";
  } catch {
    return "";
  }
};

type CategoryId = "overview" | "population" | "households" | "education" | "economy" | "migration" | "inclusion" | "family" | "identity";

const categoryDefinitions: Array<{
  id: CategoryId;
  en: string;
  ne: string;
  icon: typeof UsersThree;
}> = [
  { id: "overview", en: "Overview", ne: "समग्र झलक", icon: ChartBar },
  { id: "population", en: "Population", ne: "जनसंख्या", icon: UsersThree },
  { id: "households", en: "Households", ne: "घरपरिवार", icon: House },
  { id: "education", en: "Education", ne: "शिक्षा", icon: ChartLine },
  { id: "economy", en: "Economy & agriculture", ne: "अर्थतन्त्र र कृषि", icon: Briefcase },
  { id: "migration", en: "Migration", ne: "बसाइँसराइ", icon: AirplaneTilt },
  { id: "inclusion", en: "Health & inclusion", ne: "स्वास्थ्य र समावेशिता", icon: Heartbeat },
  { id: "family", en: "Family & fertility", ne: "परिवार र प्रजनन", icon: Baby },
  { id: "identity", en: "Language & identity", ne: "भाषा र पहिचान", icon: Translate },
];

const categoryForFamily = (family: string): CategoryId => {
  const value = family.toLowerCase();
  if (value.includes("mother_tongue") || value.includes("secondary_language")) return "identity";
  if (value.includes("first_marriage")) return "family";
  if (value.includes("distribution_ward")) return "population";
  if (value === "llc_highlight" || value.includes("age_group") || value.includes("ward_sex")) return "population";
  if (value.includes("household") || value.includes("facility") || value.includes("infrastructure")) return "households";
  if (value.includes("literacy") || value.includes("school") || value.includes("education")) return "education";
  if (value.includes("economic") || value.includes("occupation") || value.includes("industry")) return "economy";
  if (value.includes("migration") || value.includes("absent")) return "migration";
  if (value.includes("disability") || value.includes("old_age")) return "inclusion";
  if (value.includes("fertility") || value.includes("marital") || value.includes("child") || value.includes("family") || value.includes("birth") || value.includes("first_marriage") || value.includes("living_arrangement")) return "family";
  if (value.includes("language") || value.includes("religion") || value.includes("ancestor")) return "identity";
  return "overview";
};

const humanize = (value: string) =>
  value
    .replace(/^llc_/, "")
    .replace(/^api_/, "")
    .replace(/^population_/, "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const numeric = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value.replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const parseSeries = (value: unknown, fallbackName: string): NumericSeries[] => {
  if (!Array.isArray(value)) {
    if (value && typeof value === "object" && "data" in value) {
      return parseSeries([value], fallbackName);
    }
    return [];
  }
  if (value.every((item) => numeric(item) !== null)) {
    return [{ name: fallbackName, data: value.map((item) => numeric(item) ?? Number.NaN) }];
  }
  return value.flatMap((item, index) => {
    if (!item || typeof item !== "object" || !("data" in item) || !Array.isArray(item.data)) return [];
    const data = item.data.map((entry: unknown) => numeric(entry) ?? Number.NaN);
    return [{ name: "name" in item ? humanize(String(item.name)) : `${fallbackName} ${index + 1}`, data }];
  });
};

const normalizeNode = (family: string, key: string, raw: unknown): DataView | null => {
  if (!raw || typeof raw !== "object") return null;
  const node = raw as Record<string, unknown>;
  let categories = Array.isArray(node.categories) ? node.categories.map((item) => humanize(String(item))) : [];
  let series = parseSeries(node.countSeries, "Count");
  if (!series.length) series = parseSeries(node.series, "Value");

  if (!series.length) {
    const scalarEntries = Object.entries(node)
      .filter(([name, value]) => !["total", "difference", "message", "categories"].includes(name) && numeric(value) !== null)
      .slice(0, 30);
    if (scalarEntries.length >= 2) {
      categories = scalarEntries.map(([name]) => humanize(name));
      series = [{ name: "Value", data: scalarEntries.map(([, value]) => numeric(value) ?? Number.NaN) }];
    }
  }

  if (!series.length) return null;
  const maximumLength = Math.max(...series.map((item) => item.data.length));
  if (!categories.length) categories = Array.from({ length: maximumLength }, (_, index) => String(index + 1));
  categories = categories.slice(0, maximumLength);
  series = series.map((item) => ({ ...item, data: item.data.slice(0, categories.length) }));

  return {
    id: `${family}--${key}`,
    title: humanize(key === family ? family : key),
    family,
    category: categoryForFamily(family),
    categories,
    series,
    note: typeof node.message === "string" ? node.message : undefined,
    raw,
  };
};

const viewsFromBundle = (bundle: LocalDataBundle | null): DataView[] => {
  if (!bundle) return [];
  return Object.entries(bundle.datasets).flatMap(([family, dataset]) => {
    if (!dataset || typeof dataset !== "object" || Array.isArray(dataset)) return [];
    const node = dataset as Record<string, unknown>;
    if ("categories" in node || "series" in node || "countSeries" in node) {
      const view = normalizeNode(family, family, node);
      return view ? [view] : [];
    }
    return Object.entries(node).flatMap(([key, value]) => {
      const view = normalizeNode(family, key, value);
      return view ? [view] : [];
    });
  });
};

const scopeViews = (atlas: AtlasIndex, province?: Province, district?: District, local?: LocalLevel): DataView[] => {
  if (local) return [];
  const children = district?.localLevels ?? province?.districts ?? atlas.provinces;
  const label = district ? "Local level population" : province ? "District population" : "Province population";
  const categories = children.map((item) => "name" in item && typeof item.name === "object" ? item.name.en : String(item.name));
  const totals = children.map((item) => item.total);
  const males = children.map((item) => item.male);
  const females = children.map((item) => item.female);
  const ratios = children.map((item) => item.sexRatio);
  return [
    { id: "scope-population", title: label, family: "scope", category: "overview", categories, series: [{ name: "Population", data: totals }], raw: children },
    { id: "scope-sex", title: "Population by sex", family: "scope", category: "population", categories, series: [{ name: "Male", data: males }, { name: "Female", data: females }], raw: children },
    { id: "scope-ratio", title: "Sex ratio by place", family: "scope", category: "population", categories, series: [{ name: "Male per 100 female", data: ratios }], note: "Male per 100 female", raw: children },
  ];
};

const palette = ["#df3f32", "#3f6f60", "#7b938a", "#e98c66", "#a8bcb4", "#24483e"];

const formatNumber = (value: number, language: Language) =>
  Number.isFinite(value) ? new Intl.NumberFormat(language === "ne" ? "ne-NP" : "en-US", { maximumFractionDigits: 2 }).format(value) : "—";

const chartTypeForView = (view: DataView): ChartType => {
  const numericCategories = view.categories.length > 5 && view.categories.every((item) => /^\d+(?:\.\d+)?$/.test(item.replace(/,/g, "")));
  if (view.series.length === 1 && view.categories.length <= 6) return "donut";
  if (numericCategories || /age|ward|period|month/i.test(`${view.title} ${view.family}`)) return "line";
  return "bar";
};

const compactView = (view: DataView, limit: number): DataView => ({
  ...view,
  categories: view.categories.slice(0, limit),
  series: view.series.map((series) => ({ ...series, data: series.data.slice(0, limit) })),
});

function DonutChart({ view, language }: { view: DataView; language: Language }) {
  const values = view.series[0].data.slice(0, 6);
  const labels = view.categories.slice(0, 6);
  const total = values.reduce((sum, value) => sum + (Number.isFinite(value) ? Math.max(0, value) : 0), 0) || 1;
  const [active, setActive] = useState<number | null>(null);
  let cursor = 0;
  const segments = values.map((value) => {
    const start = cursor;
    const percentage = (Number.isFinite(value) ? Math.max(0, value) : 0) / total * 100;
    cursor += percentage;
    return { start, percentage };
  });
  return (
    <div className="donut-chart">
      <div className="donut-visual">
        <svg className="donut-ring" viewBox="0 0 140 140" role="img" aria-label={view.title} onPointerLeave={() => setActive(null)}>
          <circle className="donut-track" cx="70" cy="70" r="52" pathLength="100" />
          {segments.map((segment, index) => (
            <circle
              key={labels[index]}
              className="donut-segment"
              cx="70"
              cy="70"
              r="52"
              pathLength="100"
              stroke={palette[index % palette.length]}
              strokeDasharray={`${Math.max(0, segment.percentage - 0.7)} ${100 - Math.max(0, segment.percentage - 0.7)}`}
              strokeDashoffset={-segment.start}
              style={{ animationDelay: `${index * 0.07}s` }}
              tabIndex={0}
              role="button"
              aria-label={`${labels[index]}, ${formatNumber(values[index], language)}, ${segment.percentage.toFixed(1)} percent`}
              onPointerEnter={() => setActive(index)}
              onFocus={() => setActive(index)}
              onBlur={() => setActive(null)}
            />
          ))}
          <text x="70" y="67" textAnchor="middle" className="donut-total">{formatNumber(total, language)}</text>
          <text x="70" y="85" textAnchor="middle" className="donut-total-label">Total</text>
        </svg>
        {active !== null && <div className="chart-tooltip donut-tooltip" role="status"><strong>{labels[active]}</strong><span>{view.series[0].name}<b>{formatNumber(values[active], language)}</b></span><small>{segments[active].percentage.toFixed(1)}% of total</small></div>}
      </div>
      <div className="donut-legend">{labels.map((label, index) => <button type="button" key={label} onPointerEnter={() => setActive(index)} onPointerLeave={() => setActive(null)} onFocus={() => setActive(index)} onBlur={() => setActive(null)}><i style={{ background: palette[index % palette.length] }} /><span>{label}</span><strong>{formatNumber(values[index], language)}</strong></button>)}</div>
    </div>
  );
}

function BarChart({ view, language, selectedWard }: { view: DataView; language: Language; selectedWard: string }) {
  const maximum = Math.max(1, ...view.series.flatMap((item) => item.data.filter(Number.isFinite).map(Math.abs)));
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const activeCategory = activeIndex === null ? null : view.categories[activeIndex];
  return (
    <div className="bar-chart" onPointerLeave={() => setActiveIndex(null)}>
      <div className={`chart-readout ${activeIndex === null ? "is-idle" : ""}`} role="status">
        <strong>{activeCategory ?? (language === "ne" ? "चार्टमाथि सार्नुहोस्" : "Move across the chart")}</strong>
        <span>{activeIndex === null ? (language === "ne" ? "ठ्याक्कै मान हेर्नुहोस्" : "Exact values appear here") : view.series.slice(0, 3).map((series) => <b key={series.name}>{series.name} <i>{formatNumber(series.data[activeIndex] ?? 0, language)}</i></b>)}</span>
      </div>
      {view.categories.map((category, categoryIndex) => (
        <div className={selectedWard && category.replace(/\D/g, "") === selectedWard ? "bar-row ward-active" : "bar-row"} key={`${category}-${categoryIndex}`} onPointerEnter={() => setActiveIndex(categoryIndex)}>
          <span title={category}>{category}</span>
          <div className="bar-series">{view.series.slice(0, 3).map((series, seriesIndex) => {
            const value = series.data[categoryIndex] ?? Number.NaN;
            const plottedValue = Number.isFinite(value) ? value : 0;
            return <button type="button" key={series.name} className="bar-value" style={{ width: `${Math.max(1.5, (Math.abs(plottedValue) / maximum) * 100)}%`, background: palette[seriesIndex % palette.length], animationDelay: `${categoryIndex * 0.035 + seriesIndex * 0.055}s` }} aria-label={`${category}, ${series.name}, ${formatNumber(value, language)}`} onFocus={() => setActiveIndex(categoryIndex)} onBlur={() => setActiveIndex(null)}><span className="bar-inline-value">{formatNumber(value, language)}</span></button>;
          })}</div>
        </div>
      ))}
      <div className="series-key bar-series-key">{view.series.slice(0, 3).map((series, index) => <span key={series.name}><i style={{ background: palette[index % palette.length] }} />{series.name}</span>)}</div>
    </div>
  );
}

function LineChart({ view, language }: { view: DataView; language: Language }) {
  const width = 640;
  const height = 240;
  const padding = 28;
  const values = view.series.flatMap((item) => item.data).filter(Number.isFinite);
  const maximum = Math.max(...values, 1);
  const minimum = Math.min(...values, 0);
  const range = maximum - minimum || 1;
  const length = Math.max(...view.series.map((item) => item.data.length), 2);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const pointList = (data: number[]) => data.map((value, index) => ({
    x: padding + (index / (length - 1)) * (width - padding * 2),
    y: height - padding - (((Number.isFinite(value) ? value : 0) - minimum) / range) * (height - padding * 2),
    value,
    category: view.categories[index] ?? String(index + 1),
  }));
  const points = (data: number[]) => pointList(data).map((point) => `${point.x},${point.y}`).join(" ");
  const firstSeriesPoints = pointList(view.series[0]?.data ?? []);
  const areaPoints = firstSeriesPoints.length ? `${padding},${height - padding} ${firstSeriesPoints.map((point) => `${point.x},${point.y}`).join(" ")} ${firstSeriesPoints.at(-1)?.x ?? width - padding},${height - padding}` : "";
  const activeX = activeIndex === null ? null : padding + (activeIndex / (length - 1)) * (width - padding * 2);
  return (
    <div className="line-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={view.title} onPointerMove={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * width;
        setActiveIndex(Math.max(0, Math.min(length - 1, Math.round(((x - padding) / (width - padding * 2)) * (length - 1)))));
      }} onPointerLeave={() => setActiveIndex(null)}>
        <defs><linearGradient id={`area-${view.id.replace(/[^a-z0-9]/gi, "-")}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={palette[0]} stopOpacity=".22" /><stop offset="100%" stopColor={palette[0]} stopOpacity="0" /></linearGradient></defs>
        {[0, 1, 2, 3].map((line) => <line key={line} x1={padding} x2={width - padding} y1={padding + line * 58} y2={padding + line * 58} />)}
        {areaPoints && <polygon className="line-area" points={areaPoints} fill={`url(#area-${view.id.replace(/[^a-z0-9]/gi, "-")})`} />}
        {activeX !== null && <line className="line-hover-guide" x1={activeX} x2={activeX} y1={padding} y2={height - padding} />}
        {view.series.slice(0, 3).map((series, index) => <polyline key={series.name} points={points(series.data)} style={{ stroke: palette[index % palette.length], animationDelay: `${index * 0.13}s` }} />)}
        {view.series.slice(0, 3).flatMap((series, seriesIndex) => pointList(series.data).map((point, pointIndex) => <circle key={`${series.name}-${pointIndex}`} className="line-point" cx={point.x} cy={point.y} r="5" fill={palette[seriesIndex % palette.length]} style={{ animationDelay: `${0.25 + pointIndex * 0.025 + seriesIndex * 0.08}s` }} tabIndex={0} role="button" aria-label={`${point.category}, ${series.name}, ${formatNumber(point.value, language)}`} onFocus={() => setActiveIndex(pointIndex)} onBlur={() => setActiveIndex(null)} />))}
      </svg>
      <div className={`chart-readout line-readout ${activeIndex === null ? "is-idle" : ""}`} role="status"><strong>{activeIndex === null ? (language === "ne" ? "चार्टको कुनै पनि भागमा सार्नुहोस्" : "Move anywhere across the chart") : view.categories[activeIndex]}</strong><span>{activeIndex === null ? (language === "ne" ? "ठ्याक्कै मान हेर्नुहोस्" : "Every series value will appear here") : view.series.slice(0, 3).map((series) => <b key={series.name}>{series.name} <i>{formatNumber(series.data[activeIndex] ?? 0, language)}</i></b>)}</span></div>
      <div className="line-category-grid">{view.categories.map((category, index) => <button type="button" key={`${category}-${index}`} onPointerEnter={() => setActiveIndex(index)} onPointerLeave={() => setActiveIndex(null)} onFocus={() => setActiveIndex(index)} onBlur={() => setActiveIndex(null)} title={`${category}: ${view.series.map((series) => `${series.name} ${formatNumber(series.data[index] ?? 0, language)}`).join(" · ")}`}><span>{category}</span><strong>{view.series.map((series) => `${series.name}: ${formatNumber(series.data[index] ?? 0, language)}`).join(" · ")}</strong></button>)}</div>
      <div className="series-key">{view.series.slice(0, 3).map((series, index) => <span key={series.name}><i style={{ background: palette[index % palette.length] }} />{series.name}</span>)}</div>
      <span className="sr-only">Range {formatNumber(minimum, language)} to {formatNumber(maximum, language)}</span>
    </div>
  );
}

function DataTable({ view, language, showAll = false }: { view: DataView; language: Language; showAll?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const maxima = view.series.map((series) => Math.max(1, ...series.data.filter(Number.isFinite).map((value) => Math.abs(value))));
  const denseColumns = view.series.length > 3;
  const visibleCategories = showAll || expanded ? view.categories : view.categories.slice(0, 12);
  return (
    <div className={`data-table-wrap ${denseColumns ? "is-dense-columns" : ""}`}>
      <table>
        <caption className="sr-only">{view.title}</caption>
        <thead><tr><th scope="col"><span>{language === "ne" ? "श्रेणी" : "Category"}</span></th>{view.series.map((series) => <th scope="col" key={series.name}><span>{series.name}</span></th>)}</tr></thead>
        <tbody>{visibleCategories.map((category, index) => <tr key={`${category}-${index}`} style={{ animationDelay: `${Math.min(index, 18) * 0.028}s` }}><th scope="row"><span className="table-category">{category}</span></th>{view.series.map((series, seriesIndex) => {
          const value = series.data[index] ?? 0;
          const plottedValue = Number.isFinite(value) ? value : 0;
          return <td key={series.name}><div className="table-value"><i style={{ width: `${(Math.abs(plottedValue) / maxima[seriesIndex]) * 100}%` }} /><span>{formatNumber(value, language)}</span></div></td>;
        })}</tr>)}</tbody>
      </table>
      {!showAll && view.categories.length > 12 && <button type="button" className="load-more" onClick={() => setExpanded((value) => !value)}>{expanded ? "Show fewer rows" : `See all ${view.categories.length} rows`}<span>{expanded ? "↑" : "↓"}</span></button>}
    </div>
  );
}

const csvForView = (view: DataView) => {
  const escape = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;
  const rows = [["Category", ...view.series.map((item) => item.name)], ...view.categories.map((category, index) => [category, ...view.series.map((item) => item.data[index] ?? 0)])];
  return rows.map((row) => row.map(escape).join(",")).join("\n");
};

const escapeHtml = (value: string | number) => String(value)
  .replaceAll("&", "&amp;")
  .replaceAll("<", "&lt;")
  .replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;")
  .replaceAll("'", "&#039;");

const standaloneHtmlReport = ({ scopeName, views, language, summary }: {
  scopeName: string;
  views: DataView[];
  language: Language;
  summary?: { population: number; male: number; female: number; sexRatio: number };
}) => {
  const isNepali = language === "ne";
  const generated = new Intl.DateTimeFormat(isNepali ? "ne-NP" : "en-US", { dateStyle: "long", timeStyle: "short" }).format(new Date());
  const sections = views.map((view, viewIndex) => {
    const maxima = view.series.map((series) => Math.max(1, ...series.data.map((value) => Math.abs(value))));
    const rows = view.categories.map((category, categoryIndex) => `<tr><th scope="row">${escapeHtml(category)}</th>${view.series.map((series, seriesIndex) => {
      const value = series.data[categoryIndex] ?? 0;
      const magnitude = Math.min(100, (Math.abs(value) / maxima[seriesIndex]) * 100);
      return `<td><div class="value-cell" style="--m:${magnitude.toFixed(2)}%"><span>${escapeHtml(formatNumber(value, language))}</span></div></td>`;
    }).join("")}</tr>`).join("");
    return `<section class="dataset" id="dataset-${viewIndex + 1}"><header><div><small>${escapeHtml(humanize(view.family))}</small><h2>${escapeHtml(view.title)}</h2></div><span>${view.categories.length} ${isNepali ? "श्रेणी" : "categories"}</span></header><div class="table-wrap"><table><caption>${escapeHtml(view.title)}</caption><thead><tr><th>${isNepali ? "श्रेणी" : "Category"}</th>${view.series.map((series) => `<th>${escapeHtml(series.name)}</th>`).join("")}</tr></thead><tbody>${rows}</tbody></table></div><footer>National Population and Housing Census 2021 · NSO Nepal</footer></section>`;
  }).join("");
  const summaryHtml = summary ? `<section class="summary">${[
    [isNepali ? "जनसंख्या" : "Population", summary.population],
    [isNepali ? "पुरुष" : "Male", summary.male],
    [isNepali ? "महिला" : "Female", summary.female],
    [isNepali ? "लैङ्गिक अनुपात" : "Sex ratio", summary.sexRatio],
  ].map(([label, value]) => `<article><span>${escapeHtml(label)}</span><strong>${escapeHtml(formatNumber(Number(value), language))}</strong></article>`).join("")}</section>` : "";
  return `<!doctype html><html lang="${language}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${escapeHtml(scopeName)} · Census 2021 report</title><style>
  :root{--ink:#13231f;--muted:#66736e;--line:#d9e0db;--paper:#f7f8f4;--accent:#df3f32;--wash:#edf2ee;color-scheme:light}*{box-sizing:border-box}body{margin:0;background:var(--paper);color:var(--ink);font-family:Inter,ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif;font-size:15px;line-height:1.5}main{width:min(1180px,calc(100% - 32px));margin:auto;padding:48px 0 80px}.report-head{padding:34px 0 42px;border-bottom:2px solid var(--ink)}.eyebrow{margin:0 0 16px;color:var(--accent);font-size:11px;font-weight:800;letter-spacing:.12em;text-transform:uppercase}.report-head h1{max-width:900px;margin:0;font-size:clamp(2.4rem,7vw,6.4rem);line-height:.9;letter-spacing:-.065em}.report-head>p:last-child{max-width:760px;margin:24px 0 0;color:var(--muted)}.summary{display:grid;grid-template-columns:repeat(4,1fr);border-bottom:1px solid var(--line)}.summary article{min-width:0;padding:24px 20px;border-right:1px solid var(--line)}.summary article:last-child{border-right:0}.summary span{display:block;color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:.06em}.summary strong{display:block;margin-top:12px;font-size:clamp(1.5rem,4vw,3.2rem);line-height:1;font-variant-numeric:tabular-nums}.report-meta{display:flex;flex-wrap:wrap;justify-content:space-between;gap:12px;padding:18px 0 42px;color:var(--muted);font-size:11px}.dataset{margin:0 0 24px;border:1px solid var(--line);border-radius:16px;background:#fff;overflow:hidden;break-inside:avoid;box-shadow:0 12px 30px rgba(28,48,42,.05)}.dataset>header{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;padding:22px 24px;border-bottom:1px solid var(--line)}.dataset header small{color:var(--accent);font-size:9px;font-weight:800;letter-spacing:.09em;text-transform:uppercase}.dataset h2{margin:6px 0 0;font-size:21px;line-height:1.15;letter-spacing:-.035em}.dataset header>span,.dataset footer{color:var(--muted);font-size:10px}.dataset footer{padding:12px 24px;border-top:1px solid var(--line)}.table-wrap{overflow:auto}table{width:100%;border-collapse:collapse;font-size:12px}caption{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0)}th,td{min-width:120px;padding:11px 14px;border-bottom:1px solid #e8ece9;text-align:right}th:first-child{text-align:left;position:sticky;left:0;background:#fff;z-index:1}thead th{background:var(--wash);color:#4e5c57;font-size:10px;letter-spacing:.04em;text-transform:uppercase}thead th:first-child{background:var(--wash);z-index:2}tbody th{font-weight:650}.value-cell{position:relative;min-height:25px;display:flex;align-items:center;justify-content:flex-end;isolation:isolate}.value-cell:before{content:"";position:absolute;z-index:-1;right:-5px;top:1px;bottom:1px;width:var(--m);border-radius:5px 0 0 5px;background:linear-gradient(90deg,rgba(223,63,50,.03),rgba(223,63,50,.17))}.value-cell span{font-weight:650;font-variant-numeric:tabular-nums}.report-foot{margin-top:44px;padding-top:22px;border-top:1px solid var(--ink);display:flex;justify-content:space-between;gap:20px;color:var(--muted);font-size:11px}@media(max-width:700px){main{width:min(100% - 20px,1180px);padding-top:24px}.summary{grid-template-columns:repeat(2,1fr)}.summary article:nth-child(2){border-right:0}.summary article:nth-child(-n+2){border-bottom:1px solid var(--line)}.dataset>header{padding:18px;flex-direction:column}.dataset header>span{margin-top:-10px}th,td{padding:10px;min-width:105px}.report-foot{flex-direction:column}}@media print{body{background:#fff}main{width:100%;padding:0}.report-head{padding-top:0}.dataset{box-shadow:none;border-radius:8px;page-break-inside:avoid}.report-meta{padding-bottom:20px}.no-print{display:none!important}@page{size:A4;margin:13mm}}
  </style></head><body><main><header class="report-head"><p class="eyebrow">Nepal Open Census Atlas · Standalone report</p><h1>${escapeHtml(scopeName)}</h1><p>${isNepali ? "राष्ट्रिय जनगणना २०२१ का चयनित तथ्याङ्कहरू। यो फाइल अफलाइन खुल्छ र ब्राउजरबाट PDF मा प्रिन्ट गर्न सकिन्छ।" : "Selected National Population and Housing Census 2021 data. This file works offline and can be printed to PDF from any browser."}</p></header>${summaryHtml}<div class="report-meta"><span>${views.length} ${isNepali ? "अन्तरक्रियात्मक दृश्यका तालिका" : "data views rendered as readable tables"}</span><span>${isNepali ? "तयार गरिएको" : "Generated"}: ${escapeHtml(generated)}</span></div>${sections}<footer class="report-foot"><span>Source: National Population and Housing Census 2021 · National Statistics Office Nepal</span><span>Created by Sudip Bhatta · Nepal Open Census Atlas</span></footer></main></body></html>`;
};

const download = (filename: string, content: string, type: string) => {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

function DataVisual({ view, language, selectedWard, mode, showAll = false }: {
  view: DataView;
  language: Language;
  selectedWard: string;
  mode: ViewMode;
  showAll?: boolean;
}) {
  const chartType = chartTypeForView(view);
  if (mode === "table") return <DataTable view={view} language={language} showAll={showAll} />;
  if (chartType === "donut") return <DonutChart view={view} language={language} />;
  if (chartType === "line") return <LineChart view={view} language={language} />;
  return <BarChart view={view} language={language} selectedWard={selectedWard} />;
}

function DataDetailDialog({ view, language, selectedWard, scopeName, onClose, onChat }: {
  view: DataView;
  language: Language;
  selectedWard: string;
  scopeName: string;
  onClose: () => void;
  onChat: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [mode, setMode] = useState<ViewMode>("chart");
  const [zoom, setZoom] = useState(100);
  const chartType = chartTypeForView(view);
  const ChartIcon = chartType === "donut" ? ChartDonut : chartType === "line" ? ChartLine : ChartBar;

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    element.showModal();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
      if (element.open) element.close();
    };
  }, []);

  return (
    <dialog
      ref={dialog}
      className="data-detail-dialog"
      aria-labelledby="detail-view-title"
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <section className="data-detail-shell">
        <header className="data-detail-header">
          <div>
            <span>{humanize(view.family)} · {scopeName}</span>
            <h2 id="detail-view-title">{view.title}</h2>
            <p>{language === "ne" ? `${view.categories.length} श्रेणी र ${view.series.length} शृङ्खलाको पूर्ण दृश्य` : `Complete view with ${view.categories.length} categories and ${view.series.length} series`}</p>
          </div>
          <button type="button" className="detail-close" onClick={onClose} aria-label={language === "ne" ? "बन्द गर्नुहोस्" : "Close full view"}><X size={21} /></button>
        </header>
        <div className="data-detail-toolbar" aria-label={language === "ne" ? "दृश्य नियन्त्रण" : "View controls"}>
          <div className="detail-mode-switch">
            <button type="button" className={mode === "chart" ? "is-active" : ""} onClick={() => setMode("chart")}><ChartIcon size={17} />{language === "ne" ? "चार्ट" : "Chart"}</button>
            <button type="button" className={mode === "table" ? "is-active" : ""} onClick={() => setMode("table")}><Table size={17} />{language === "ne" ? "तालिका" : "Table"}</button>
          </div>
          <button type="button" className="detail-ai-button" onClick={onChat}><ChatCircleDots size={18} />{language === "ne" ? "AI सँग कुरा गर्नुहोस्" : "Chat with AI"}</button>
          <div className="detail-zoom" aria-label={language === "ne" ? "जुम नियन्त्रण" : "Zoom controls"}>
            <button type="button" onClick={() => setZoom((value) => Math.max(70, value - 10))} disabled={zoom <= 70} aria-label="Zoom out"><Minus size={16} /></button>
            <button type="button" className="zoom-value" onClick={() => setZoom(100)} aria-label="Reset zoom">{zoom}%</button>
            <button type="button" onClick={() => setZoom((value) => Math.min(160, value + 10))} disabled={zoom >= 160} aria-label="Zoom in"><Plus size={16} /></button>
          </div>
          <div className="detail-export-actions">
            <button type="button" onClick={() => download(`${view.id}.html`, standaloneHtmlReport({ scopeName: `${scopeName} · ${view.title}`, views: [view], language }), "text/html;charset=utf-8")}><FileHtml size={17} />HTML</button>
            <button type="button" onClick={() => download(`${view.id}.csv`, csvForView(view), "text/csv;charset=utf-8")}><FileCsv size={17} />CSV</button>
          </div>
        </div>
        <div className="data-detail-viewport">
          <div className="data-detail-zoom-layer" style={{ transform: `scale(${zoom / 100})`, width: `${10000 / zoom}%` }}>
            <DataVisual view={view} language={language} selectedWard={selectedWard} mode={mode} showAll />
          </div>
        </div>
        <footer className="data-detail-footer"><span>National Population and Housing Census 2021</span><span>National Statistics Office Nepal</span></footer>
      </section>
    </dialog>
  );
}

function AiChatPanel({ view, language, scopeName, onClose }: {
  view: DataView;
  language: Language;
  scopeName: string;
  onClose: () => void;
}) {
  const storageKey = `atlas-ai:${language}:${scopeName}:${view.id}`;
  const storedMessages = (() => {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(storageKey) || "[]") as ChatMessage[];
      return Array.isArray(parsed) ? parsed.slice(-12) : [];
    } catch {
      return [];
    }
  })();
  const [messages, setMessages] = useState<ChatMessage[]>(storedMessages);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [webSearch, setWebSearch] = useState(true);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const messageEnd = useRef<HTMLDivElement>(null);
  const controller = useRef<AbortController | null>(null);
  const autoSummaryRequested = useRef(storedMessages.some((message) => message.role === "assistant"));
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      controller.current?.abort();
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    try {
      if (messages.length) sessionStorage.setItem(storageKey, JSON.stringify(messages.slice(-12)));
    } catch {
      // Session persistence is best effort when storage is unavailable.
    }
  }, [messages, storageKey]);

  useEffect(() => {
    const scrollContainer = messageEnd.current?.parentElement;
    if (scrollContainer) scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const ask = async (question: string, searchWeb = webSearch) => {
    const cleanQuestion = question.trim();
    if (!cleanQuestion || sending) return;
    const userMessage: ChatMessage = { role: "user", content: cleanQuestion };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput("");
    setSending(true);
    setError("");
    controller.current = new AbortController();
    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.current.signal,
        body: JSON.stringify({
          messages: nextMessages.slice(-8),
          language,
          webSearch: searchWeb,
          context: {
            id: view.id,
            title: view.title,
            family: view.family,
            scope: scopeName,
            categories: view.categories,
            series: view.series,
            note: view.note,
            source: "National Population and Housing Census 2021, National Statistics Office Nepal",
          },
        }),
      });
      const responseText = await response.text();
      let payload: { message?: string; error?: string; sources?: ChatSource[] };
      try {
        payload = JSON.parse(responseText) as { message?: string; error?: string; sources?: ChatSource[] };
      } catch {
        throw new Error(response.status === 404
          ? "The local AI endpoint is not running. Use Cloudflare Pages dev for API testing, or deploy the Functions endpoint."
          : `The AI endpoint returned an invalid response (${response.status}).`);
      }
      if (!response.ok || !payload.message) throw new Error(payload.error || "The AI assistant could not answer.");
      setMessages((current) => [...current, { role: "assistant", content: payload.message ?? "", sources: payload.sources }]);
    } catch (requestError) {
      if (requestError instanceof DOMException && requestError.name === "AbortError") return;
      setError(requestError instanceof Error ? requestError.message : "The AI assistant could not answer.");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (autoSummaryRequested.current) return;
    autoSummaryRequested.current = true;
    const summaryPrompt = language === "ne"
      ? `हाल देखाइएको ${scopeName} को ${view.title} डेटासेटको छोटो सारांश दिनुहोस्। मुख्य संख्या, सबैभन्दा ठूलो/सानो वर्ग र देखिएको महत्वपूर्ण कुरा उल्लेख गर्नुहोस्।`
      : `Summarize the currently displayed ${view.title} dataset for ${scopeName}. Mention the key totals, largest and smallest categories, and one important observation. Use only the displayed data.`;
    void ask(summaryPrompt, false);
  }, [language, scopeName, view.id]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void ask(input);
  };

  const suggestions = language === "ne"
    ? ["यो तथ्याङ्क सारांश गर्नुहोस्", "सबैभन्दा ठूलो श्रेणी कुन हो?", "शृङ्खलाबीचको फरक बुझाउनुहोस्"]
    : ["Summarize this dataset", "Which category is largest?", "Explain the differences between series"];

  return (
    <div className="ai-chat-layer" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <aside className={minimized ? "ai-chat-panel is-minimized" : "ai-chat-panel"} role="dialog" aria-modal="true" aria-labelledby="ai-chat-title">
        <header>
          <div className="ai-chat-mark"><Sparkle size={20} weight="fill" /></div>
          <div><span>{language === "ne" ? "तथ्याङ्क सहायक" : "Data assistant"}</span><h2 id="ai-chat-title">{view.title}</h2><p>{scopeName}</p></div>
          <div className="ai-header-actions"><button type="button" onClick={() => setMinimized((value) => !value)} aria-label={minimized ? "Expand AI chat" : "Minimize AI chat"}>{minimized ? <Plus size={20} /> : <Minus size={20} />}</button><button type="button" onClick={onClose} aria-label={language === "ne" ? "AI च्याट बन्द गर्नुहोस्" : "Close AI chat"}><X size={20} /></button></div>
        </header>
        <div className="ai-context-note"><ChatCircleDots size={17} /><span>{language === "ne" ? "यो कुराकानी हालको तालिका र त्यसका मानहरूमा सीमित छ।" : "This conversation is grounded in the current dataset and its displayed values."}</span></div>
        <div className="ai-messages" aria-live="polite">
            {messages.map((message, index) => <div key={`${message.role}-${index}`} className={`ai-message is-${message.role}`}><span>{message.role === "assistant" ? "AI" : language === "ne" ? "तपाईं" : "You"}</span><p>{message.content}</p>{message.sources?.length ? <div className="ai-sources"><strong>{language === "ne" ? "समर्थनकारी वेब सन्दर्भहरू" : "Supporting web references"}</strong><small>{language === "ne" ? "माथिको उत्तर यहीँ तयार गरिएको हो; यी लिङ्कहरू थप सन्दर्भका लागि मात्र हुन्।" : "The answer above was compiled here; these links are optional supporting references."}</small>{message.sources.flatMap((source) => { const url = safeExternalUrl(source.url); return url ? [<a key={url} href={url} target="_blank" rel="noopener noreferrer">{source.title}<ArrowRight size={13} /></a>] : []; })}</div> : null}</div>)}
          {sending && <div className="ai-message is-assistant is-loading"><span>AI</span><p><CircleNotch size={17} />{language === "ne" ? "तथ्याङ्क पढ्दै…" : "Reading the dataset…"}</p></div>}
          <div ref={messageEnd} />
        </div>
        {messages.length === 0 && !sending && <div className="ai-suggestions">{suggestions.map((suggestion) => <button type="button" key={suggestion} onClick={() => void ask(suggestion)}>{suggestion}</button>)}</div>}
        {error && <p className="ai-error" role="alert">{error}</p>}
          <button type="button" className={webSearch ? "ai-web-toggle is-on" : "ai-web-toggle"} onClick={() => setWebSearch((value) => !value)} disabled={sending}><GlobeHemisphereWest size={16} />{webSearch ? (language === "ne" ? "वेब खोज र सारांश सक्रिय" : "Web search & summary on") : (language === "ne" ? "वेबबाट पनि खोज्नुहोस्" : "Search and summarize the web")}</button>
        <form className="ai-composer" onSubmit={submit}>
          <label className="sr-only" htmlFor={`chat-${view.id}`}>{language === "ne" ? "तथ्याङ्कबारे प्रश्न" : "Question about this dataset"}</label>
          <input ref={inputRef} id={`chat-${view.id}`} value={input} onChange={(event) => setInput(event.target.value)} maxLength={1000} placeholder={language === "ne" ? "यो तथ्याङ्कबारे सोध्नुहोस्…" : "Ask about this dataset…"} disabled={sending} />
          <button type="submit" disabled={sending || !input.trim()} aria-label={language === "ne" ? "पठाउनुहोस्" : "Send message"}><PaperPlaneTilt size={19} weight="fill" /></button>
        </form>
        <footer>{language === "ne" ? "AI ले गल्ती गर्न सक्छ। महत्त्वपूर्ण निष्कर्ष मूल स्रोतसँग जाँच्नुहोस्।" : "AI can make mistakes. Verify important conclusions against the original source."}</footer>
      </aside>
    </div>
  );
}

function DataViewCard({ view, language, selectedWard, scopeName, onOpenDetail, onOpenChat }: {
  view: DataView;
  language: Language;
  selectedWard: string;
  scopeName: string;
  onOpenDetail: () => void;
  onOpenChat: () => void;
}) {
  const [mode, setMode] = useState<ViewMode>("chart");
  const card = useRef<HTMLElement>(null);
  const rotateXTo = useRef<((value: number) => void) | null>(null);
  const rotateYTo = useRef<((value: number) => void) | null>(null);
  const scaleXTo = useRef<((value: number) => void) | null>(null);
  const scaleYTo = useRef<((value: number) => void) | null>(null);
  const chartType = chartTypeForView(view);
  const previewLimit = chartType === "bar" ? 7 : chartType === "line" ? 9 : 6;
  const hasMore = view.categories.length > previewLimit || view.series.length > 4;
  const preview = hasMore ? compactView(view, previewLimit) : view;
  const motionVariant = [...view.id].reduce((sum, character) => sum + character.charCodeAt(0), 0) % 3;
  const ChartIcon = chartType === "donut" ? ChartDonut : chartType === "line" ? ChartLine : ChartBar;
  const readingGuide = language === "ne"
    ? mode === "table" ? "ठ्याक्कै मान र सापेक्ष परिमाण एउटै पङ्क्तिमा पढ्नुहोस्।" : "ठ्याक्कै मान हेर्न चार्टको चिन्हमा होभर, ट्याप वा किबोर्ड फोकस गर्नुहोस्।"
    : mode === "table" ? "Read the exact figure and its relative magnitude in the same row." : "Hover, tap, or keyboard-focus any chart mark to reveal its exact figure.";

  useGSAP(() => {
    const media = gsap.matchMedia();
    media.add("(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)", () => {
      if (!card.current) return;
      gsap.set(card.current, { transformPerspective: 1100, transformOrigin: "50% 50%" });
      rotateXTo.current = gsap.quickTo(card.current, "rotationX", { duration: 0.42, ease: "power3.out" });
      rotateYTo.current = gsap.quickTo(card.current, "rotationY", { duration: 0.42, ease: "power3.out" });
      scaleXTo.current = gsap.quickTo(card.current, "scaleX", { duration: 0.35, ease: "power3.out" });
      scaleYTo.current = gsap.quickTo(card.current, "scaleY", { duration: 0.35, ease: "power3.out" });
    });
    return () => media.revert();
  }, { scope: card });

  useGSAP(() => {
    if (!card.current || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const target = card.current.querySelector(".view-body > *");
    if (!target) return;
    const from = motionVariant === 0 ? { autoAlpha: 0, y: 16, scale: 0.985 } : motionVariant === 1 ? { autoAlpha: 0, x: -16, rotationY: -2 } : { autoAlpha: 0, y: -10, scale: 0.96 };
    gsap.fromTo(target, from, { autoAlpha: 1, x: 0, y: 0, scale: 1, rotationY: 0, duration: 0.62, ease: "power3.out", clearProps: "transform,opacity,visibility" });
  }, { dependencies: [mode], scope: card, revertOnUpdate: true });

  const handleTilt = (event: ReactPointerEvent<HTMLElement>) => {
    if (!rotateXTo.current || !rotateYTo.current) return;
    const rect = event.currentTarget.getBoundingClientRect();
    rotateYTo.current(((event.clientX - rect.left) / rect.width - 0.5) * 3.2);
    rotateXTo.current(-((event.clientY - rect.top) / rect.height - 0.5) * 2.4);
    scaleXTo.current?.(1.006);
    scaleYTo.current?.(1.006);
  };

  const resetTilt = () => {
    rotateXTo.current?.(0);
    rotateYTo.current?.(0);
    scaleXTo.current?.(1);
    scaleYTo.current?.(1);
  };

  return (
    <article ref={card} className={`data-view-card chart-${chartType} motion-${motionVariant}`} onPointerMove={handleTilt} onPointerLeave={resetTilt}>
      <header>
        <div><span>{humanize(view.family)}</span><h3>{view.title}</h3>{view.note && <p>{view.note}</p>}</div>
        <div className="view-actions">
          <button className={mode === "chart" ? "is-active" : ""} onClick={() => setMode("chart")} aria-label="Chart view"><ChartIcon size={16} /></button>
          <button className={mode === "table" ? "is-active" : ""} onClick={() => setMode("table")} aria-label="Table view"><Table size={16} /></button>
          <button onClick={onOpenDetail} aria-label="Open full view" title="Open full view"><ArrowsOut size={16} /></button>
          <button className="chat-with-ai-action" onClick={onOpenChat} aria-label="Chat with AI about this dataset" title="Chat with AI"><ChatCircleDots size={17} /></button>
          <button onClick={() => download(`${view.id}.html`, standaloneHtmlReport({ scopeName: `${scopeName} · ${view.title}`, views: [view], language }), "text/html;charset=utf-8")} aria-label="Download standalone HTML report" title="Download standalone HTML report"><FileHtml size={16} /></button>
          <button onClick={() => download(`${view.id}.csv`, csvForView(view), "text/csv;charset=utf-8")} aria-label="Download CSV" title="Download CSV"><FileCsv size={16} /></button>
        </div>
      </header>
      <div className={`view-body ${hasMore ? "has-more-data" : ""}`}>
        <DataVisual view={preview} language={language} selectedWard={selectedWard} mode={mode} />
        {hasMore && <button type="button" className="show-full-view" onClick={onOpenDetail}><ArrowsOut size={17} /><span>{language === "ne" ? "पूरा दृश्य हेर्नुहोस्" : "See full view"}</span><small>{language === "ne" ? `सबै ${view.categories.length} श्रेणी` : `All ${view.categories.length} categories`}</small></button>}
      </div>
      <p className="view-reading-guide">{readingGuide}</p>
      <footer><span>{view.categories.length} categories</span><span>{view.series.length} series</span><span>NSO Nepal · 2021</span></footer>
    </article>
  );
}

export default function DataExplorer({
  atlas,
  province,
  district,
  local,
  language,
  selectedWard,
  fullPage = false,
  onOpenFull,
  onBack,
}: {
  atlas: AtlasIndex;
  province?: Province;
  district?: District;
  local?: LocalLevel;
  language: Language;
  selectedWard: string;
  fullPage?: boolean;
  onOpenFull?: () => void;
  onBack?: () => void;
}) {
  const [bundle, setBundle] = useState<LocalDataBundle | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [activeCategory, setActiveCategory] = useState<CategoryId>("overview");
  const [search, setSearch] = useState("");
  const [detailView, setDetailView] = useState<DataView | null>(null);
  const [chatView, setChatView] = useState<DataView | null>(null);
  const container = useRef<HTMLElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    setActiveCategory("overview");
    setSearch("");
    setLoading(true);
    setLoadError(false);
    setBundle(null);
    setDetailView(null);
    setChatView(null);
    const bundlePath = local && province && district
      ? `/data/local/p${province.id}-d${district.id}-m${local.id}.json`
      : district
        ? `/data/scope/district-${district.id}.json`
        : province
          ? `/data/scope/province-${province.id}.json`
          : "/data/scope/national.json";
    fetch(bundlePath, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error("Dataset bundle unavailable");
        return response.json();
      })
      .then((data) => setBundle(data))
      .catch((error) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) setLoadError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [province, district, local]);

  const views = useMemo(() => bundle ? viewsFromBundle(bundle) : scopeViews(atlas, province, district, local), [atlas, province, district, local, bundle]);
  const categoryCounts = useMemo(() => Object.fromEntries(categoryDefinitions.map((category) => [category.id, views.filter((view) => view.category === category.id).length])), [views]);
  const filtered = views.filter((view) => (activeCategory === "overview" ? true : view.category === activeCategory) && `${view.title} ${view.family}`.toLowerCase().includes(search.toLowerCase()));
  const currentName = local?.name ?? district?.name ?? province?.name[language] ?? (language === "ne" ? "नेपाल" : "Nepal");
  const currentSummary = local ?? district ?? province ?? atlas.national;

  useEffect(() => {
    const root = container.current;
    if (!root) return;
    const cards = root.querySelectorAll<HTMLElement>(".data-view-card");
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      cards.forEach((card) => card.classList.add("is-revealed"));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-revealed");
        observer.unobserve(entry.target);
      });
    }, { rootMargin: "80px 0px -8%", threshold: 0.08 });
    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, [activeCategory, search, bundle, fullPage]);

  const exportAll = () => {
    const reportViews = fullPage ? filtered : views;
    const report = standaloneHtmlReport({
      scopeName: currentName,
      views: reportViews,
      language,
      summary: { population: currentSummary.total, male: currentSummary.male, female: currentSummary.female, sexRatio: currentSummary.sexRatio },
    });
    download(`${currentName.toLowerCase().replace(/\W+/g, "-")}-census-2021-report.html`, report, "text/html;charset=utf-8");
  };

  return (
    <section id="data-library" ref={container} className={`data-library section-shell ${fullPage ? "is-full-page" : "is-preview"}`} aria-labelledby="data-library-title">
      {fullPage && <button type="button" className="library-back" onClick={onBack}><ArrowLeft size={18} />{language === "ne" ? "मुख्य पृष्ठमा फर्कनुहोस्" : "Back to atlas"}</button>}
      <div className="data-library-heading">
        {fullPage && <button type="button" className="back-to-top" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Back to top">↑</button>}
        <div><p className="kicker">03 / Data library</p><h2 id="data-library-title">{language === "ne" ? `${currentName} को डाटा` : `All data for ${currentName}`}</h2><p>{`${views.length} interactive views from ${Object.keys(bundle?.datasets ?? {}).length} collected dataset families.`}{bundle?.meta.calculatedFromLocalProfiles ? " Rollup values are calculated from the verified local-profile records." : ""}</p></div>
        <button className="button primary" onClick={exportAll} disabled={loading || !views.length}><FileHtml size={18} />{language === "ne" ? "पढ्न मिल्ने HTML रिपोर्ट" : "Export readable HTML report"}</button>
      </div>

      {fullPage && <div className="data-toolbar">
        <label className="data-search"><MagnifyingGlass size={18} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={language === "ne" ? "सूचक खोज्नुहोस्" : "Search indicators and datasets"} /></label>
        <div className="data-count"><strong>{views.length}</strong><span>{language === "ne" ? "दृश्य उपलब्ध" : "visualizations available"}</span></div>
      </div>}

      {fullPage && <div className="category-strip" role="tablist" aria-label="Dataset categories">
        {categoryDefinitions.map((category) => {
          const Icon = category.icon;
          const count = category.id === "overview" ? views.length : categoryCounts[category.id] ?? 0;
          return <button key={category.id} role="tab" aria-selected={activeCategory === category.id} className={activeCategory === category.id ? "is-active" : ""} onClick={() => setActiveCategory(category.id)} disabled={category.id !== "overview" && !count}><Icon size={18} /><span>{language === "ne" ? category.ne : category.en}</span><small>{count}</small></button>;
        })}
      </div>}

      {loading && <div className="data-loading" aria-live="polite"><span /><div><strong>Loading the selected census archive</strong><p>Only this geography's partition is being downloaded.</p></div></div>}
      {loadError && <div className="data-empty"><Heartbeat size={26} /><h3>Dataset bundle unavailable</h3><p>Rebuild the data index and try again.</p></div>}
      {!loading && !loadError && !filtered.length && <div className="data-empty"><MagnifyingGlass size={26} /><h3>No matching indicators</h3><p>Try another category or search phrase.</p></div>}
      {!loading && !loadError && <div className="data-view-grid">{(fullPage ? filtered : filtered.slice(0, 4)).map((view) => <DataViewCard key={view.id} view={view} language={language} selectedWard={selectedWard} scopeName={currentName} onOpenDetail={() => setDetailView(view)} onOpenChat={() => setChatView(view)} />)}</div>}
      {!loading && !fullPage && filtered.length > 4 && <button className="open-full-library" onClick={onOpenFull}>{language === "ne" ? `सबै ${filtered.length} दृश्य खोल्नुहोस्` : `Explore all ${filtered.length} data views`} <ArrowRight size={17} /><span>{language === "ne" ? "छुट्टै डाटा पुस्तकालय" : "Open the full data library"}</span></button>}
      {detailView && <DataDetailDialog view={detailView} language={language} selectedWard={selectedWard} scopeName={currentName} onClose={() => setDetailView(null)} onChat={() => { setChatView(detailView); setDetailView(null); }} />}
      {chatView && <AiChatPanel key={`${chatView.id}-${language}`} view={chatView} language={language} scopeName={currentName} onClose={() => setChatView(null)} />}
    </section>
  );
}
